// @flow
import React, { useRef, useEffect } from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { t } from 'javascripts/utils/withTranslation';
import Icon from 'components/_particles/icon/react/Icon';

type CommentPreviewProps = {
  subject: string,
  text: string,
  userName?: string,
  onSubmit: () => void,
  onEdit: () => void,
};

const CommentPreview = ({
  subject,
  text,
  userName = 'Ohne Name',
  onSubmit,
  onEdit,
}: CommentPreviewProps): React$MixedElement => {
  const articleRef = useRef<?HTMLElement>(null);

  moment.locale('de');
  const date = moment().format('DD.MM.YYYY');
  const time = moment().format('HH:mm');
  const dateTime = `${date} ${time}`;

  const sanitizedText = DOMPurify.sanitize(text);

  useEffect(() => {
    if (articleRef.current) {
      window.scrollTo({
        top: articleRef.current.offsetTop - 30,
        behavior: 'smooth',
      });
      articleRef.current?.focus();
    }
  }, []);

  return (
    <article
      ref={articleRef}
      className="comment comment--highlighted comment--preview"
      tabIndex={0}
    >
      <div className="comment__meta">
        <div className="comment__author comment__author-badge">
          <Icon icon="profile" className="comment__badge-icon" />
          <span className="comment__author-name">{userName}</span>
        </div>

        <time className="comment__date-time" dateTime={dateTime}>
          <Icon icon="calendar" className="comment__meta-icon" title="Datum" />
          {date}
          <Icon icon="clock" className="comment__meta-icon" title="Zeitpunkt" />
          {time}
        </time>
      </div>

      <div className="comment__body">
        <div className="comment__headline">
          <div className="comment__preview-kicker">{t('Vorschau')}</div>
          <h4 className="headline headline--4">
            {subject === '' ? (
              <span className="comment__headline--empty">
                {t('Der Betreff ist leer')}
              </span>
            ) : (
              subject
            )}
          </h4>
        </div>
        <div className="comment__text">
          <div className="comment__text-preview">
            {text === '' ? (
              <span className="comment__text--empty">
                {t('Ihr Kommentar ist leer.')}
              </span>
            ) : (
              <div
                className="text"
                dangerouslySetInnerHTML={{ __html: sanitizedText }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="comment__footer">
        <div className="comment__preview-tools">
          <button
            type="button"
            className="button button--secondary"
            onClick={onEdit}
          >
            <span className="button__text">{t('Bearbeiten')}</span>
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={onSubmit}
          >
            <span className="button__text">{t('Veröffentlichen')}</span>
          </button>
        </div>
      </div>
    </article>
  );
};

export default CommentPreview;
